// BUILD-046 task 8.15, rebuilt in correction cycle 1bb88b3e (stage B2b, item N2): the Assign task dialog - ONE shared dialog and ONE request contract
// (POST /work/assign) for every entry point (design "Assign task dialog"; Req 3.1 - 3.3, 3.7, 3.10 - 3.16, 3.18, 3.20, 13.1).
//
// All behavior lives in assignModel.ts (state and rules), assignFlow.ts (the preview and submit flows), peopleComboboxModel.ts (the combobox keys) and
// dialogFocusModel.ts (the focus trap); all are tested. This component draws them:
//   ONE clean form (Owner-approved layout, October 20; no "More details"; re-ordered by the Owner on October 21), in this order:
//   1. Task title (wide) with a compact, LIVE Priority (Low, Medium, High, Urgent: the same four choices as Edit, from priorityChoices.ts; Medium by default;
//      sent as `priority` and saved by POST /work/assign on every task it creates). There is NO Status control: every new task starts Active (Owner decision 2026-10-20), and status is changed from Edit afterwards;
//   2. Assign to, immediately below: a searchable people multi-select (type a name or open the list; the chosen people are removable chips) with an explicit "Assign to me"
//      link at the right end of the label row. The dialog opens with NOBODY selected: "Assign to me" ADDS the Caller (one click, then Assign task). Tab, with text typed and a
//      name highlighted, adds that person and moves on to the next field (peopleComboboxModel; Owner direction 2026-10-20 night);
//   3. Work groups as compact checkboxes, FOUR per row from 768px (md), two per row from 640px and one column below; wrapping, long names stay readable;
//   4. the assignment-mode choice, only when the recorded rule shows it (more than one person or any group; hidden for one person; neither option preselected);
//   5. Customer (or, with customers off, Customer / Job) (about 60%) and Task due date (about 40%) SIDE BY SIDE, stacked (association first) below 640px:
//      - Customer (optional, BUILD-046 customer association), only when the host passes `customersEnabled` (`GET /work/me` `canSearchCustomers`): a searchable single choice
//        (CustomerField.tsx, customerFieldModel.ts) over the limited customer directory. It takes the 60% slot, and the JOB picker is then NOT drawn (this slice offers no
//        project/job search and no disabled placeholder). The request carries the stable `customerId` only; the server checks it and stores the name. Its results span the
//        whole row. Typed text that was never chosen stops the submit with a message beside the field, so a customer is never dropped silently. Tab and typing never choose
//        a customer. Below the picker (pricingLinkModel.ts) "Not in the list? Add it in Pricing" is a link to Pricing, in a new tab so this dialog and its draft stay open, ONLY
//        when the server said `canAddCustomerInPricing` and a Pricing address is configured; otherwise it is a plain note that the task can go without a customer (or, when the server said `pricingCustomerAccess: 'unavailable'`, a note that Pricing access could not be checked). After a
//        visit to Pricing, "Search again" (or coming back to this window) asks the directory again so a just-added customer can be found;
//      - Customer / Job (optional, BUILD-046 J1a), only when customers are NOT enabled and `GET /work/me` says `canSearchJobs` (`jobsEnabled`): exactly as before - a searchable single
//        choice (JobField.tsx, jobFieldModel.ts) over the limited job directory. The request carries the stable `jobId` only. Typed text that was never chosen stops the submit
//        with a message beside the field. Tab and typing never choose a job. A customer choice never reads or writes the job, and the other way round;
//      - Task due date (optional): the date input with small Today, Tomorrow, Next week and Clear buttons beneath it (QuickDateField, stacked layout);
//   6. Description (optional), starting at two rows and resizable (no microphone: there is no reusable dictation control in this app);
//   7. Collaborators (optional), below Description: the same people field with a search icon.
//   - there is no Next action field here any more, and none is sent (it is still set from Edit);
//   - a refused submit names each problem BESIDE its own field (linked with aria-describedby) and moves focus to the first one; nothing is sent;
//   - the footer (Cancel and Assign task) is pinned: only the body scrolls, so the buttons stay reachable. The dialog uses the width it has (up to 42rem),
//     traps Tab, closes with Escape (discarding, as before; an open people list closes first) and returns focus to the control that opened it.
// While a submission runs, Submit is disabled and says Saving; on failure a message shows and every value stays; the submission id is held for the life of
// the dialog. Plain fields only: no dictation, no source prefill (slice S2). The date field holds a `YYYY-MM-DD` string and is never parsed with `new Date()`.
//
// The dialog closes itself when the Caller changes (the previous Caller's entries must not be sent as the new Caller).
import { useCallback, useEffect, useRef, useState } from 'react';
import { notifySharedTasksChanged, onCallerChanged, workApi } from '../../api/workShared';
import type { AssignResponse, PricingCustomerAccess, WorkEmployeeRef, WorkGroupRef } from '../../api/workShared';
import { issuedRequests } from './issuedRequestsStore';
import { addEmployee, assignToMe, clearCustomer, clearJob, closeAssign, closedAssignState, describeAssign, groupOptions, missingFields, openAssign, removeEmployee, selectionKey, setCustomer, setField, setJob, setMode, setPriority, toggleCollaborator, toggleGroup } from './assignModel';
import type { AssignMode, AssignState, MissingField, SourceRefValue } from './assignModel';
import { runPreview, runSubmit } from './assignFlow';
import type { AssignStore } from './assignFlow';
import { describeList, initialListState, isStaleOutcome, listReducer, toListError } from './listStateModel';
import type { ListState } from './listStateModel';
import { ListStateView } from './ListStateView';
import { JobField } from './JobField';
import { CustomerField } from './CustomerField';
import { PricingNote } from './PricingNote';
import { decidePricingNote, shouldSearchAgainOnReturn } from './pricingLinkModel';
import { planCustomerSearch } from './customerFieldModel';
import { config } from '../../config';
import { PeopleCombobox } from './PeopleCombobox';
import { QuickDateField } from './QuickDateField';
import { useDialogFocus } from './useDialogFocus';
import { TASK_DUE_DATE_LABEL } from './dateLabels';
// D1 display cleanup: the group names are shown without the "(FICTIONAL)" test marker (what is sent, the group ids, is unchanged).
import { stripTestMarker } from './displayNames';

export interface AssignTaskDialogProps {
  open: boolean;
  /** The Caller's canonical Employee ID (from `GET /work/me`); used only by the explicit "Assign to me" choice. The dialog opens with nobody selected. */
  callerEmployeeId: string | null;
  /** The source record this task is about, when the dialog is opened from a source workspace (prefill itself is slice S2). */
  sourceRef?: SourceRefValue | null;
  /**
   * Offer the optional Customer / Job field (BUILD-046 J1a). The host passes `GET /work/me` `canSearchJobs`: false (or a server that does not say) leaves the
   * dialog exactly as it was, with no job field and no `jobId` in the request. It is drawn only while `customersEnabled` is false.
   */
  jobsEnabled?: boolean;
  /**
   * Offer the optional Customer field (BUILD-046 customer association). The host passes `GET /work/me` `canSearchCustomers`: false (or a server that does not say) leaves
   * the dialog exactly as it was (the job field per `jobsEnabled`, no customer field, no `customerId` in the request). When true the Customer picker takes the slot beside
   * Task due date and the job picker is not drawn.
   */
  customersEnabled?: boolean;
  /**
   * `GET /work/me` `canAddCustomerInPricing`: the server says the person has Pricing access. Together with `config.pricingUrl` it only decides whether "Not in the list?" is a
   * link to Pricing or a plain note (pricingLinkModel.ts). Advice for the UI: false or absent is the plain note.
   */
  canAddCustomerInPricing?: boolean;
  /**
   * `GET /work/me` `pricingCustomerAccess`: `unavailable` means the server could not check Pricing access, so the note says so instead of the "no access" sentence (never a link). Absent
   * (an older summary) is decided from `canAddCustomerInPricing` alone. Advice for the UI; Pricing enforces on click.
   */
  pricingCustomerAccess?: PricingCustomerAccess;
  onClose: () => void;
  /** Every ACCEPTED submission, even when the dialog was closed while it ran, so the host can refresh its lists. */
  onIssued?: (response: AssignResponse, title: string) => void;
}

const PREVIEW_DELAY_MS = 300;

/** The limited job directory lookup (`GET /work/jobs`). */
const searchJobs = (q: string) => workApi.searchJobs(q);
/** The limited customer directory lookup (`GET /work/customers`). */
const searchCustomers = (q: string) => workApi.searchCustomers(q);

/** Beside the job field when text was typed there and no job was chosen: the job would otherwise be dropped without a word. */
const JOB_DRAFT_PROBLEM = 'Choose a job from the list, or clear the search box. The task was not assigned.';
const JOB_NOT_AVAILABLE_PROBLEM = 'That job is not available. Choose another job or remove it.';
/** Beside the customer field when text was typed there and no customer was chosen: the customer would otherwise be dropped without a word. */
const CUSTOMER_DRAFT_PROBLEM = 'Choose a customer from the list, or clear the search box. The task was not assigned.';
const CUSTOMER_NOT_AVAILABLE_PROBLEM = 'That customer is not available. Choose another customer or remove it.';

/** A fresh submission id. `crypto.randomUUID` needs a secure context (localhost and 127.0.0.1 are); the fallback builds a v4 UUID. */
export function newSubmissionId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c !== undefined && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c !== undefined && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** A list the dialog needs (people, groups) with the list-state model: a failure is never an empty list, and Retry reloads only it. */
export function useOptionList<T>(load: () => Promise<readonly T[]>, active: boolean): [ListState<T>, () => void] {
  const [state, setState] = useState<ListState<T>>(initialListState<T>());
  const seq = useRef(0);
  const reload = useCallback(() => {
    const epoch = workApi.getCallerEpoch();
    seq.current += 1;
    const mine = seq.current;
    setState((s) => listReducer(s, { type: 'request', epoch, seq: mine }));
    load().then(
      (items) => setState((s) => listReducer(s, { type: 'success', epoch, seq: mine, items })),
      (thrown: unknown) => {
        if (!isStaleOutcome(thrown)) setState((s) => listReducer(s, { type: 'failure', epoch, seq: mine, error: toListError(thrown) }));
      }
    );
  }, [load]);
  useEffect(() => {
    if (active) reload();
  }, [active, reload]);
  return [state, reload];
}

const loadPeople = () => workApi.listEmployees().then((r) => r.employees);
const loadGroups = () => workApi.listGroups().then((r) => r.groups);

/** The element that takes focus for each kind of problem (the first control of the field). */
const PROBLEM_FOCUS: Readonly<Record<MissingField, string>> = Object.freeze({ recipients: 'assign-people', mode: 'assign-mode-anyone', title: 'assign-title' });

const INPUT = 'w-full px-3 py-2 border rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE]';

export function AssignTaskDialog({ open, callerEmployeeId, sourceRef = null, jobsEnabled = false, customersEnabled = false, canAddCustomerInPricing = false, pricingCustomerAccess, onClose, onIssued }: AssignTaskDialogProps) {
  const [state, setState] = useState<AssignState>(closedAssignState());
  // Text typed in the job field and not chosen. A ref, because the submit reads it at the moment of sending; the flag shows the problem beside the field.
  const jobDraft = useRef('');
  const [jobDraftProblem, setJobDraftProblem] = useState(false);
  // The same for the customer field (BUILD-046 customer association). Separate from the job's: neither reads or clears the other.
  const customerDraft = useRef('');
  const [customerDraftProblem, setCustomerDraftProblem] = useState(false);
  // "Search again" after a visit to Pricing: the field asks the directory again each time this number moves. `followedPricing` is set by the link and consumed by the next
  // time this window is focused (or by the Search again button), so ordinary window switching never re-runs a search.
  const [searchAgainToken, setSearchAgainToken] = useState(0);
  const followedPricing = useRef(false);
  const latest = useRef<AssignState>(state);
  const store = useRef<AssignStore>({
    get: () => latest.current,
    set: (next) => {
      latest.current = next;
      setState(next);
    },
  }).current;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onIssuedRef = useRef(onIssued);
  onIssuedRef.current = onIssued;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // Escape closes the dialog (discarding, as before) wherever focus is; the open people list closes first (it handles that Escape itself).
  const escapeRef = useRef<() => void>(() => undefined);
  const focus = useDialogFocus(open, dialogRef, () => escapeRef.current());

  const [people, reloadPeople] = useOptionList<WorkEmployeeRef>(loadPeople, open);
  const [groups, reloadGroups] = useOptionList<WorkGroupRef>(loadGroups, open);

  // Opening gives a clean dialog with NOBODY selected and a fresh submission id; closing discards everything.
  useEffect(() => {
    if (open) store.set(openAssign(newSubmissionId(), sourceRef));
    else store.set(closeAssign());
    // The dialog is (re)opened when `open` turns true; the caller and source are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A Caller change while open: discard the dialog (nothing the previous Caller entered is sent as the new Caller) and tell the host.
  useEffect(
    () =>
      onCallerChanged(() => {
        if (latest.current.open) {
          store.set(closeAssign());
          onCloseRef.current();
        }
      }),
    [store]
  );

  // Back from Pricing: when the person followed the "Add it in Pricing" link and this window is focused again, the customer search runs again for what is typed, so a
  // customer just added there can be found (the dialog and its draft never closed). Nothing happens for ordinary window switching, or while a customer is chosen.
  useEffect(() => {
    if (!open || !customersEnabled) {
      followedPricing.current = false;
      return undefined;
    }
    const onFocus = () => {
      const searchable = planCustomerSearch(customerDraft.current).kind === 'search';
      const again = shouldSearchAgainOnReturn({ followedPricingLink: followedPricing.current, customerChosen: latest.current.customer !== null, hasSearchText: searchable });
      followedPricing.current = false;
      if (again) setSearchAgainToken((n) => n + 1);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [open, customersEnabled]);

  // The server preview for the current selection, after a short pause so a burst of ticks sends one request.
  const key = selectionKey(state);
  useEffect(() => {
    if (!state.open) return undefined;
    const timer = setTimeout(() => {
      void runPreview(workApi, store);
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [key, state.open, store]);

  // Focus goes to the first wanted control when the form appears (the people field), and to Close when the result appears.
  const showingResult = state.open && state.phase === 'succeeded' && state.result !== null;
  useEffect(() => {
    if (state.open) focus.focusFirst();
    // focusFirst reads the dialog element; it is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open, showingResult]);
  // The submit button is disabled while a request runs, which drops focus onto the page; after a failure focus returns to it so the person can retry or Escape.
  const failed = state.open && state.phase === 'failed';
  useEffect(() => {
    if (failed) focus.focusId('assign-submit');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  if (!state.open) return null;

  const view = describeAssign(state, { callerEmployeeId });
  const disabled = view.inputsDisabled;
  const peopleView = describeList(people, 'people', 'There are no people to choose from.');
  const groupsView = describeList(groups, 'work groups', 'There are no work groups yet.');
  const problemOf = (field: MissingField) => view.problems.find((p) => p.field === field) ?? null;
  const recipientsProblem = problemOf('recipients');
  const modeProblem = problemOf('mode');
  const titleProblem = problemOf('title');
  // Beside the job field: typed text that was not chosen, or a job the server refused (the footer carries the full message either way).
  const jobProblem = jobDraftProblem ? JOB_DRAFT_PROBLEM : view.failure !== null && view.failure.type === 'job-not-available' && state.job !== null ? JOB_NOT_AVAILABLE_PROBLEM : null;
  // Beside the customer field: typed text that was not chosen, or a customer the server refused (the footer carries the full message either way).
  const customerProblem = customerDraftProblem ? CUSTOMER_DRAFT_PROBLEM : view.failure !== null && view.failure.type === 'customer-not-available' && state.customer !== null ? CUSTOMER_NOT_AVAILABLE_PROBLEM : null;
  // "Not in the list?" below the Customer picker: a link to Pricing, a plain note, or nothing (pricingLinkModel.ts; the only inputs are the server flag and the configured address).
  const pricingNote = decidePricingNote({ customersEnabled, canAddCustomerInPricing, pricingCustomerAccess, pricingUrl: config.pricingUrl });
  // The recipient count line shows only when it matters (the same condition as the mode choice: more than one person, or any group); a failed check always shows.
  const previewText =
    view.preview.status === 'failed'
      ? 'Could not check recipients. You can still submit; the server checks again.'
      : !view.modeChoice.visible
        ? ''
        : view.preview.status === 'loading'
          ? 'Checking who can receive this…'
          : view.preview.status === 'ready' && view.preview.eligibleCount !== null
            ? `${view.preview.eligibleCount} eligible ${view.preview.eligibleCount === 1 ? 'recipient' : 'recipients'}.`
            : '';

  function close(): void {
    if (latest.current.phase === 'saving') return;
    store.set(closeAssign());
    onClose();
  }
  escapeRef.current = close;

  async function submit(): Promise<void> {
    // Text typed in the job field and never chosen would be dropped without a word: stop and say so beside the field (only when nothing else is missing,
    // so the usual missing-field message and focus still come first).
    if (jobsEnabled && !customersEnabled && latest.current.job === null && jobDraft.current !== '' && missingFields(latest.current).length === 0 && latest.current.phase !== 'saving') {
      setJobDraftProblem(true);
      document.getElementById('assign-job')?.focus();
      return;
    }
    // The same for text typed in the customer field and never chosen.
    if (customersEnabled && latest.current.customer === null && customerDraft.current !== '' && missingFields(latest.current).length === 0 && latest.current.phase !== 'saving') {
      setCustomerDraftProblem(true);
      document.getElementById('assign-customer')?.focus();
      return;
    }
    // The names of the groups selected at the moment of sending, for the wording on the issued row (the selection can change while it runs).
    const selectedGroupIds = latest.current.groupIds;
    const groupNames = groups.phase === 'data' ? groups.items.filter((g) => selectedGroupIds.includes(g.groupId)).map((g) => g.name) : [];
    // The job chosen at the moment of sending, for the hint row of Assigned by Me (the server's own row replaces it on the next read).
    const sentJob = latest.current.job;
    // The customer chosen at the moment of sending, for the same hint row.
    const sentCustomer = latest.current.customer;
    const outcome = await runSubmit(workApi, store, newSubmissionId);
    if (!outcome.sent) {
      // Refused before sending: focus moves to the first field with a problem (the problem text sits beside it).
      const first = describeAssign(latest.current, { callerEmployeeId }).problems[0];
      if (first !== undefined) document.getElementById(PROBLEM_FOCUS[first.field])?.focus();
      return;
    }
    if (outcome.ok) {
      // An accepted Everyone request, or an Anyone request that named a group, is listed under "Requests I issued"; the host refreshes its
      // lists and counts.
      void issuedRequests.track(outcome.response, outcome.title, { groupNames, job: sentJob, customer: sentCustomer });
      notifySharedTasksChanged({ reason: 'assign', requestId: outcome.response.requestId });
      if (onIssuedRef.current) onIssuedRef.current(outcome.response, outcome.title);
    }
  }

  const groupItems = groupsView.kind === 'data' ? groupOptions(groupsView.items, state) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-2 sm:items-center sm:p-6">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-task-heading"
        tabIndex={-1}
        className="flex max-h-[calc(100vh-1rem)] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl focus:outline-none sm:max-h-[calc(100vh-3rem)]"
        data-testid="assign-task-dialog"
      >
        <div className="flex flex-shrink-0 items-start justify-between px-5 pb-1 pt-4">
          <h2 id="assign-task-heading" className="text-xl font-bold text-gray-900">
            Assign task
          </h2>
          <button type="button" onClick={close} aria-label="Close" title="Close" className="rounded px-1 text-xl leading-none text-gray-500 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]">
            &times;
          </button>
        </div>

        {view.result !== null ? (
          <div className="space-y-3 px-5 pb-5 pt-2" data-testid="assign-result">
            <p role="status" aria-live="polite" className="rounded bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
              {view.result.headline}
              {view.result.aggregateLabel !== null && <span className="ml-2 text-xs">({view.result.aggregateLabel})</span>}
              {view.result.replayed && <span className="ml-2 text-xs">(already sent; nothing was duplicated)</span>}
            </p>
            {view.result.notices.length > 0 && (
              <ul className="list-inside list-disc space-y-0.5 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {view.result.notices.map((notice) => (
                  <li key={notice}>{notice}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => store.set(openAssign(newSubmissionId(), sourceRef))} className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]">
                Assign another
              </button>
              <button type="button" data-autofocus="" onClick={close} className="rounded bg-[#4AABDE] px-4 py-2 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#4AABDE]">
                Close
              </button>
            </div>
          </div>
        ) : (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 pb-3 pt-2" data-testid="assign-body">
              {/* ───── Row 1: Task title (wide) and a compact, live Priority ───── */}
              {/* From 640px up the two sit on one row (the title takes what the 8rem Priority select leaves); below that each has its own row. Priority is a real
                  choice (Low, Medium, High, Urgent; Medium by default) that POST /work/assign saves on every task it creates. There is no Status control: every new task
                  starts Active, and status is changed from Edit afterwards. */}
              <div>
                <div className="grid grid-cols-1 items-start gap-x-3 gap-y-2.5 sm:grid-cols-[minmax(0,1fr)_8rem]" data-testid="assign-title-row">
                  <div>
                    <label htmlFor="assign-title" className="mb-1 block text-xs font-medium text-gray-700">
                      Task title <span aria-hidden="true" className="text-red-600">*</span>
                    </label>
                    <input
                      id="assign-title"
                      type="text"
                      value={state.title}
                      disabled={disabled}
                      aria-required="true"
                      aria-invalid={titleProblem !== null || undefined}
                      aria-describedby={titleProblem !== null ? 'assign-title-error' : undefined}
                      onChange={(e) => store.set(setField(latest.current, 'title', e.target.value))}
                      className={`${INPUT} ${titleProblem !== null ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {titleProblem !== null && (
                      <p id="assign-title-error" role="alert" className="mt-1 text-sm text-red-700" data-testid="assign-problem-title">
                        {titleProblem.text}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="assign-priority" className="mb-1 block text-xs font-medium text-gray-700">
                      Priority
                    </label>
                    <select
                      id="assign-priority"
                      value={view.priority.value}
                      disabled={disabled}
                      onChange={(e) => store.set(setPriority(latest.current, e.target.value))}
                      className={`${INPUT} border-gray-300`}
                      data-testid="assign-priority"
                    >
                      {view.priority.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* ───── Assign to (required), immediately below the title row ───── */}
              <div>
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <label id="assign-people-label" htmlFor="assign-people" className="text-xs font-medium text-gray-700">
                    Assign to <span aria-hidden="true" className="text-red-600">*</span>
                  </label>
                  {/* The dialog opens with nobody selected; this is the explicit one-click choice for a task for yourself. */}
                  {view.assignToMe.available && (
                    <button
                      type="button"
                      disabled={disabled || view.assignToMe.selected}
                      onClick={() => store.set(assignToMe(latest.current, callerEmployeeId ?? ''))}
                      className="rounded px-1 text-xs font-medium text-[#1f6a99] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:text-gray-400 disabled:hover:no-underline"
                      data-testid="assign-to-me"
                    >
                      Assign to me
                    </button>
                  )}
                </div>
                <PeopleCombobox
                  id="assign-people"
                  labelledBy="assign-people-label"
                  placeholder="Type a name or choose people..."
                  people={peopleView}
                  onRetry={reloadPeople}
                  selectedIds={state.employeeIds}
                  disabled={disabled}
                  onAdd={(employeeId) => store.set(addEmployee(latest.current, employeeId))}
                  onRemove={(employeeId) => store.set(removeEmployee(latest.current, employeeId))}
                  callerEmployeeId={callerEmployeeId}
                  describedBy={recipientsProblem !== null ? 'assign-recipients-error' : undefined}
                  invalid={recipientsProblem !== null}
                  required
                  wantsFocus
                />
                {recipientsProblem !== null && (
                  <p id="assign-recipients-error" role="alert" className="mt-1 text-sm text-red-700" data-testid="assign-problem-recipients">
                    {recipientsProblem.text}
                  </p>
                )}
              </div>

              {/* ───── Work groups: compact checkboxes, FOUR per row from 768px (md), two from 640px, one below; they wrap and a long name wraps inside its cell ───── */}
              <fieldset aria-describedby={recipientsProblem !== null ? 'assign-recipients-error' : undefined}>
                <legend className="mb-1 text-xs font-medium text-gray-700">Work groups</legend>
                <ListStateView
                  view={groupsView}
                  listLabel="work groups"
                  onRetry={reloadGroups}
                  renderItems={() => (
                    <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 md:grid-cols-4" data-testid="assign-groups">
                      {groupItems.map((group) => (
                        <li key={group.groupId} className="min-w-0">
                          <label className="flex items-start gap-2 text-sm text-gray-800">
                            <input type="checkbox" className="mt-0.5 flex-shrink-0" checked={group.selected} disabled={disabled} onChange={() => store.set(toggleGroup(latest.current, group.groupId))} />
                            <span className="min-w-0 break-words">{stripTestMarker(group.name)}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                />
              </fieldset>

              {/* ───── How should this be completed? Only when more than one person is selected or a group is checked (the recorded rule in assignModel) ───── */}
              {view.modeChoice.visible && (
                <fieldset aria-describedby={modeProblem !== null ? 'assign-mode-error' : undefined} className="rounded border border-gray-200 px-3 py-1.5" data-testid="assign-mode-choice">
                  <legend className="px-1 text-xs font-medium text-gray-700">How should this be completed? (required)</legend>
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {view.modeChoice.options.map((option) => (
                      <label key={option.value} className="flex items-center gap-2 text-sm text-gray-800">
                        <input id={`assign-mode-${option.value}`} type="radio" name="assign-mode" value={option.value} checked={option.checked} disabled={disabled} onChange={() => store.set(setMode(latest.current, option.value as AssignMode))} />
                        {option.label}
                      </label>
                    ))}
                  </div>
                  {modeProblem !== null && (
                    <p id="assign-mode-error" role="alert" className="mt-1 text-sm text-red-700" data-testid="assign-problem-mode">
                      {modeProblem.text}
                    </p>
                  )}
                </fieldset>
              )}

              {/* The server preview of who can receive this. The count line shows only when it matters (more than one person or a group: the same condition
                  as the mode choice); a failed check, people left out and an empty group queue are always shown. */}
              {/* The live region is always present (so a later message is announced); it takes no room while it is empty, and the wrapper below is drawn only
                  when there is something to show. */}
              <div className={previewText !== '' || view.preview.excludedNotices.length > 0 || view.preview.queueEmptyAtIssue ? 'space-y-1' : 'sr-only'} data-testid="assign-preview">
                <div className="text-xs text-gray-600" role="status" aria-live="polite">
                  {previewText}
                </div>
                {view.preview.excludedNotices.length > 0 && (
                  <ul className="list-inside list-disc text-xs text-amber-800">
                    {view.preview.excludedNotices.map((notice) => (
                      <li key={notice}>{notice}</li>
                    ))}
                  </ul>
                )}
                {view.preview.queueEmptyAtIssue && <p className="text-xs text-amber-800">No one in the group can take this yet. It would wait in the queue and oversight would see it as needing reassignment.</p>}
              </div>

              {/* ───── Customer (or Customer / Job) (about 60%) and Task due date (about 40%) SIDE BY SIDE (cycle C, Owner layout 2026-10-21) ───── */}
              {/* ONE row, the existing 60/40 slot (it keeps the id the job path has always had; `data-association` says which picker it holds). The association is optional and offered only
                  when the server says the Caller may search: the CUSTOMER picker when `customersEnabled` (and then the job picker is not drawn at all - no project/job search is offered in
                  this slice, and no disabled placeholder), otherwise the Customer / Job picker when `jobsEnabled` (BUILD-046 J1a, exactly as before). With neither, the date stands alone at 40%.
                  Below 640px the two stack, the picker first. The row is the positioned ancestor of the picker's results (wideList), so they span the whole row, wrap long names, scroll
                  inside their own max height and sit above the date column and the Description. The small Today / Tomorrow / Next week / Clear buttons sit beneath the date input. */}
              <div className={`relative ${jobsEnabled || customersEnabled ? 'grid grid-cols-1 items-start gap-x-3 gap-y-3 sm:grid-cols-[3fr_2fr]' : 'sm:w-2/5'}`} data-testid="assign-job-date-row" data-association={customersEnabled ? 'customer' : jobsEnabled ? 'job' : 'none'}>
                {customersEnabled && (
                  <div className="min-w-0" data-testid="assign-customer-row">
                    <label id="assign-customer-label" htmlFor="assign-customer" className="mb-1 block text-xs font-medium text-gray-700">
                      Customer (optional)
                    </label>
                    <CustomerField
                      id="assign-customer"
                      customer={state.customer}
                      disabled={disabled}
                      search={searchCustomers}
                      onChoose={(chosen) => {
                        setCustomerDraftProblem(false);
                        store.set(setCustomer(latest.current, chosen));
                      }}
                      onClear={() => {
                        setCustomerDraftProblem(false);
                        store.set(clearCustomer(latest.current));
                      }}
                      onDraftChange={(text) => {
                        customerDraft.current = text;
                        setCustomerDraftProblem(false);
                      }}
                      problem={customerProblem}
                      searchAgainToken={searchAgainToken}
                      wideList
                    />
                    <PricingNote
                      id="assign-pricing-note"
                      note={pricingNote}
                      disabled={disabled || state.customer !== null}
                      onFollow={() => {
                        followedPricing.current = true;
                      }}
                      onSearchAgain={() => {
                        followedPricing.current = false;
                        setSearchAgainToken((n) => n + 1);
                      }}
                    />
                  </div>
                )}
                {jobsEnabled && !customersEnabled && (
                  <div className="min-w-0" data-testid="assign-job-row">
                    <label id="assign-job-label" htmlFor="assign-job" className="mb-1 block text-xs font-medium text-gray-700">
                      Customer / Job (optional)
                    </label>
                    <JobField
                      id="assign-job"
                      job={state.job}
                      disabled={disabled}
                      search={searchJobs}
                      onChoose={(chosen) => {
                        setJobDraftProblem(false);
                        store.set(setJob(latest.current, chosen));
                      }}
                      onClear={() => {
                        setJobDraftProblem(false);
                        store.set(clearJob(latest.current));
                      }}
                      onDraftChange={(text) => {
                        jobDraft.current = text;
                        setJobDraftProblem(false);
                      }}
                      problem={jobProblem}
                      wideList
                    />
                  </div>
                )}
                <div className="min-w-0" data-testid="assign-due-column">
                  <QuickDateField idPrefix="assign-due" label={TASK_DUE_DATE_LABEL} optional layout="stacked" value={state.dueDate} disabled={disabled} onChange={(value) => store.set(setField(latest.current, 'dueDate', value))} />
                </div>
              </div>

              {/* ───── Description (optional). No microphone: there is no reusable dictation control in this app, and none is shown unless it works. ───── */}
              <div>
                <label htmlFor="assign-details" className="mb-1 block text-xs font-medium text-gray-700">
                  Description (optional)
                </label>
                <textarea
                  id="assign-details"
                  rows={2}
                  value={state.details}
                  disabled={disabled}
                  placeholder="Add instructions or context..."
                  onChange={(e) => store.set(setField(latest.current, 'details', e.target.value))}
                  className={`${INPUT} resize-y border-gray-300 placeholder:text-gray-400`}
                />
              </div>

              {/* ───── Collaborators (optional): the same people field, with a search icon ───── */}
              <div>
                <label id="assign-collab-label" htmlFor="assign-collaborators" className="mb-1 block text-xs font-medium text-gray-700">
                  Collaborators (optional)
                </label>
                <PeopleCombobox
                  id="assign-collaborators"
                  labelledBy="assign-collab-label"
                  placeholder="Add collaborators..."
                  leadingIcon="search"
                  people={peopleView}
                  onRetry={reloadPeople}
                  selectedIds={state.collaborators}
                  disabled={disabled}
                  onAdd={(employeeId) => store.set(latest.current.collaborators.includes(employeeId) ? latest.current : toggleCollaborator(latest.current, employeeId))}
                  onRemove={(employeeId) => store.set(latest.current.collaborators.includes(employeeId) ? toggleCollaborator(latest.current, employeeId) : latest.current)}
                  callerEmployeeId={callerEmployeeId}
                />
              </div>
            </div>

            {/* ───── pinned footer: only the body scrolls ───── */}
            <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-2 border-t border-gray-200 px-5 py-3">
              {view.failure !== null && (
                <div role="alert" className="min-w-0 flex-1 text-sm text-red-700" data-testid="assign-failure">
                  <p className="font-medium">{view.failure.headline}</p>
                  {view.failure.detail !== null && <p className="text-xs">{view.failure.detail}</p>}
                </div>
              )}
              {view.submit.showSaving && (
                <span role="status" aria-live="polite" className="text-xs text-gray-600">
                  Saving…
                </span>
              )}
              <button type="button" onClick={close} disabled={view.submit.showSaving} className="rounded px-4 py-2 text-sm font-medium text-[#1f6a99] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50">
                Cancel
              </button>
              <button id="assign-submit" type="submit" disabled={view.submit.disabled} className="rounded bg-[#1f6a99] px-5 py-2 text-sm font-medium text-white hover:bg-[#185a84] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#4AABDE] disabled:opacity-50 disabled:hover:bg-[#1f6a99]">
                {view.submit.label}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
